<template>
  <div class="draggable-list-module">
    <v-progress-linear v-if="loading" indeterminate />

    <div v-if="!loading && !items.length" class="text-center grey--text pa-4">
      {{ display.emptyText || 'Aucun élément disponible' }}
    </div>

    <div ref="dragContainer" class="draggable-list-module__items">
      <v-tooltip
        v-for="(line, index) in items"
        :key="itemKey(line, index)"
        :disabled="!tooltip.enabled || !hasVisual(tooltip.visual)"
        :min-width="tooltip.minWidth || 320"
        :right="tooltip.position === 'right'"
        :left="tooltip.position === 'left'"
        :top="tooltip.position === 'top'"
        :bottom="tooltip.position === 'bottom'"
      >
        <template #activator="{ on, attrs }">
          <div
            v-bind="attrs"
            v-on="on"
            class="draggable-list-item app-theme-bg-card"
            :class="display.itemClass"
            :data-drag-index="index"
            :draggable="drag.enabled !== false"
            @dragstart="nativeDragStart($event, line)"
          >
            <div v-if="line.actions && line.actions.length" class="draggable-list-item__actions" @mousedown.stop @touchstart.stop @dragstart.stop.prevent>
              <v-tooltip v-for="action in line.actions" :key="action.id" bottom>
                <template #activator="{ on: actionOn, attrs: actionAttrs }">
                  <span class="d-inline-flex align-center">
                  <v-btn
                    v-bind="actionAttrs" v-on="actionOn"
                    icon small color="primary" :aria-label="action.name"
                    @click.stop="runAction(line, action)"
                  >
                    <v-icon small>{{ action.icon || 'mdi-play' }}</v-icon>
                  </v-btn>
                  <rights-indicator :checks="actionInspectionChecks(action)" :label="action.name" />
                  </span>
                </template>
                <span>{{ action.name }}</span>
              </v-tooltip>
            </div>
            <VisualRenderer
              v-for="(visual, visualIndex) in cardVisual"
              :key="visualIndex"
              :item="visual"
              :line="line"
              :page="page"
            />
          </div>
        </template>

        <div class="draggable-list-module__tooltip">
          <VisualRenderer
            v-for="(visual, visualIndex) in tooltip.visual"
            :key="visualIndex"
            :item="visual"
            :line="line"
            :page="page"
          />
        </div>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { Draggable } from '@fullcalendar/interaction'
import { client } from '../mixins'
import VisualRenderer from '@/components/engine/outils/constructVisuel/display/Renderer.vue'

const DRAG_MIME = 'application/x-lastminuproject-drag+json'

export default {
  name: 'DraggableListClient',
  mixins: [client],
  components: { VisualRenderer },
  data() {
    return {
      items: [],
      cardVisual: [],
      tooltip: { enabled: false, minWidth: 320, position: 'right', visual: [] },
      drag: { enabled: true, payloadMappings: [] },
      display: { emptyText: 'Aucun élément disponible', itemClass: '' },
      loading: false,
      fullCalendarDraggable: null,
      querys: {},
      reloadModules: [],
      actionModalPending: false,
    }
  },
  computed: {
    actionModalOpen() {
      const modal = this.$store.getters.modalShowParams({ type: 'current', val: this.modalKey })
      return !!(modal && modal.show)
    },
  },
  watch: {
    actionModalOpen(open, wasOpen) {
      if (open || !wasOpen || !this.actionModalPending) return
      this.actionModalPending = false
      this.refreshAfterAction()
    },
  },
  mounted() {
    const context = this.$store.getters.specificPage(this.modalKey) || {}
    const hasCustomParams = this.customparams && Object.keys(this.customparams).length > 0
    const hasCustomQuerys = this.customquerys && Object.keys(this.customquerys).length > 0
    this.params = hasCustomParams ? this.customparams : (context.params || {})
    this.querys = hasCustomQuerys ? this.customquerys : (context.querys || this.$route.query || {})
    this.loadItems()
  },
  beforeDestroy() {
    this.destroyDraggable()
  },
  methods: {
    // Les actions restent celles du moteur, avec les alias de la carte.
    runAction(line, action) {
      this.clickActions({
        args: line, action, idmodule: this.idmodule, page: this.page, modalKey: this.modalKey,
        navigate: navigation => {
          this.actionModalPending = navigation.type === 'modal'
          const opened = this.goTo(navigation)
          if (opened === false) this.actionModalPending = false
        },
        success: data => {
          if (data.message) this.$toast.success(data.message)
          if (action.typelink === 'api') this.refreshAfterAction()
        },
      })
    },
    // Recharge les modules liés uniquement après l'action ou la fermeture.
    refreshAfterAction() {
      const modules = [...this.reloadModules]
      return this.loadItems().then(() => {
        new Set(modules.map(Number).filter(id => Number.isInteger(id) && id > 0 && id !== Number(this.idmodule)))
          .forEach(id => this.$store.dispatch('addReloadModule', { modules: id }))
      })
    },
    loadItems() {
      this.loading = true
      return this.API({
        type: 'post',
        url: 'displaymodule',
        params: {
          id: this.idmodule,
          params: this.params,
          querys: this.querys,
        },
      }).then(response => {
        const data = response.data || {}
        this.items = Array.isArray(data.items) ? data.items : []
        this.cardVisual = Array.isArray(data.cardVisual) ? data.cardVisual : []
        this.tooltip = { ...this.tooltip, ...(data.tooltip || {}) }
        this.drag = { ...this.drag, ...(data.drag || {}) }
        this.display = { ...this.display, ...(data.display || {}) }
        this.reloadModules = Array.isArray(data.reloadModules) ? data.reloadModules : []
        this.$nextTick(this.initializeDraggable)
      }).finally(() => {
        this.loading = false
      })
    },
    initializeDraggable() {
      this.destroyDraggable()
      if (this.drag.enabled === false || this.drag.calendarEnabled === false || !this.$refs.dragContainer) return
      this.fullCalendarDraggable = new Draggable(this.$refs.dragContainer, {
        itemSelector: '.draggable-list-item',
        eventData: element => {
          const line = this.items[Number(element.dataset.dragIndex)] || {}
          const envelope = this.dragEnvelope(line)
          const event = {
            title: this.aliasValue(line, this.drag.titleAlias) || String(envelope.entityId || ''),
            create: true,
            extendedProps: { engineDrag: envelope },
          }
          const duration = this.calendarDuration(line)
          if (duration !== null) event.duration = duration
          return event
        },
      })
    },
    destroyDraggable() {
      if (this.fullCalendarDraggable && typeof this.fullCalendarDraggable.destroy === 'function') {
        this.fullCalendarDraggable.destroy()
      }
      this.fullCalendarDraggable = null
    },
    dragEnvelope(line) {
      const payload = {}
      ;(this.drag.payloadMappings || []).forEach(mapping => {
        if (!mapping || !mapping.key || !mapping.alias) return
        payload[mapping.key] = this.aliasValue(line, mapping.alias)
      })
      return {
        version: 1,
        sourceModuleId: Number(this.idmodule),
        channel: this.drag.channel || '',
        entityType: this.drag.entityType || '',
        entityId: this.aliasValue(line, this.drag.idAlias || 'id'),
        quantity: this.aliasValue(line, this.drag.quantityAlias),
        concurrencyValue: this.aliasValue(line, this.drag.concurrencyAlias),
        payload,
      }
    },
    nativeDragStart(event, line) {
      if (!event.dataTransfer) return
      const serialized = JSON.stringify(this.dragEnvelope(line))
      event.dataTransfer.effectAllowed = 'copyMove'
      event.dataTransfer.setData(DRAG_MIME, serialized)
      event.dataTransfer.setData('text/plain', serialized)
    },
    aliasValue(line, alias) {
      return alias && line && Object.prototype.hasOwnProperty.call(line, alias) ? line[alias] : null
    },
    calendarDuration(line) {
      const mode = this.drag.durationMode || (this.drag.durationAlias ? 'alias' : 'default')
      if (mode === 'default') return null

      const value = mode === 'fixed'
        ? this.drag.durationFixedHours
        : this.aliasValue(line, this.drag.durationAlias)

      if (value === null || value === undefined || value === '') return null

      // Une valeur numérique représente un nombre d'heures. FullCalendar
      // reçoit ainsi un format non ambigu : 2 -> 02:00, 1.5 -> 01:30.
      const numericValue = typeof value === 'number' || /^\d+(?:[.,]\d+)?$/.test(String(value).trim())
      if (!numericValue) return value

      const hours = Number(String(value).replace(',', '.'))
      if (!Number.isFinite(hours) || hours <= 0) return null
      const totalMinutes = Math.round(hours * 60)
      const hourPart = Math.floor(totalMinutes / 60)
      const minutePart = totalMinutes % 60
      return `${String(hourPart).padStart(2, '0')}:${String(minutePart).padStart(2, '0')}`
    },
    itemKey(line, index) {
      const id = this.aliasValue(line, this.drag.idAlias || 'id')
      return id === null || id === undefined ? `drag-item-${index}` : `drag-item-${id}`
    },
    hasVisual(visual) {
      return Array.isArray(visual) && visual.length > 0
    },
  },
}
</script>

<style scoped>
.draggable-list-module__items { display: flex; flex-direction: column; gap: 8px; }
.draggable-list-item { border: 1px solid rgba(0,0,0,.14); border-radius: 8px; padding: 10px; cursor: grab; min-width: 0; }
.draggable-list-item:active { cursor: grabbing; }
.draggable-list-item__actions { display: flex; justify-content: flex-start; gap: 2px; cursor: default; }
.draggable-list-module__tooltip { padding: 8px; }
</style>
